/**
 * Discovery — stock-screener candidate review.
 *
 * Two lanes over the same nightly factor scan:
 *   Leaders   — the trend-template gate (strict / balanced / exploratory). The promote path.
 *   Emerging  — the "emerging" preset: a fresh, volume-backed turn to a new intermediate high.
 *               An early discovery lane, not (yet) a promote path.
 *
 * The controls panel re-evaluates any threshold live over stored factors
 * (POST /api/screener/preview — a DB query, no data re-fetch).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  RefreshCw, Radar, TrendingUp, Sliders, ArrowUpRight, EyeOff,
  CheckCircle2, XCircle, Info, Rocket, Trophy,
} from 'lucide-react'
import { screener } from '../lib/api'
import MacroOpticsPanel from '../components/MacroOpticsPanel'

const mono = { fontFamily: 'JetBrains Mono, monospace' }
const card = { background: '#111827', border: '1px solid #1f2937', borderRadius: '0.75rem' }

// ── Leaders-lane controls (each carries a plain-language description) ─────────
const CONTROLS_LEADERS = [
  { key: 'min_rs_rank', label: 'Min RS rank', min: 60, max: 99, step: 1, fmt: (v) => Math.round(v),
    desc: '12-1 momentum percentile vs the vetted universe. STRICT wants ≥92 — a confirmed leader, not a name still climbing the ladder.' },
  { key: 'max_pct_below_52w_high', label: 'Max % below 52w high', min: 0.05, max: 0.30, step: 0.01, fmt: (v) => `${(v * 100).toFixed(0)}%`,
    desc: 'How far below its 52-week high the price may sit. Tight (7%) = buying strength near the pivot; loose = accepting a deeper pullback.' },
  { key: 'max_days_since_20d_high', label: 'Fresh 20d-high within', min: 1, max: 15, step: 1, fmt: (v) => `${Math.round(v)}d`,
    desc: 'Sessions since the last 20-day closing high. Small = the name is actively making highs right now.' },
  { key: 'min_excess_ret_6m', label: 'Min 6m excess vs SPY', min: -0.10, max: 0.40, step: 0.05, fmt: (v) => `${(v * 100).toFixed(0)}pp`,
    desc: '6-month total return minus SPY’s. STRICT requires +15pp of proven outperformance already banked.' },
  { key: 'min_sma200_slope_63d', label: 'Min 200-SMA slope (63d)', min: 0, max: 0.06, step: 0.005, fmt: (v) => `${(v * 100).toFixed(1)}%`,
    desc: 'Percent change in the 200-day SMA over the last quarter. Positive = the long-term trend is genuinely rising, not just flat.' },
  { key: 'min_dollar_vol', label: 'Min $ volume', min: 5e6, max: 150e6, step: 5e6, fmt: (v) => `$${(v / 1e6).toFixed(0)}M`,
    desc: '20-day average dollar volume. A liquidity floor so a promoted name can actually be traded without moving it.' },
  { key: 'min_market_cap', label: 'Min market cap', min: 2e8, max: 20e9, step: 1e8, fmt: (v) => `$${(v / 1e9).toFixed(1)}B`,
    desc: 'Market-cap floor. STRICT = $1B (established companies); looser presets reach into mid-caps.' },
  { key: 'min_cmf_63', label: 'Min money flow (63d CMF)', min: -0.10, max: 0.30, step: 0.05, fmt: (v) => v.toFixed(2),
    desc: '63-day Chaikin Money Flow — is the name under accumulation (>0) or distribution (<0) over the last quarter.' },
]

// ── Emerging-lane controls — the fresh-turn triggers ─────────────────────────
const CONTROLS_EMERGING = [
  { key: 'min_ret_21d', label: 'Min 1-month return', min: -0.20, max: 0.50, step: 0.01, fmt: (v) => `${(v * 100).toFixed(0)}%`,
    desc: 'The move must be real. Requires at least this 21-session return — the actual thrust, not a wobble.' },
  { key: 'min_ret_5d', label: 'Min 5-day return', min: -0.15, max: 0.20, step: 0.01, fmt: (v) => `${(v * 100).toFixed(0)}%`,
    desc: 'Still advancing this week. A small negative floor allows a normal pause; a sharp 5-day drop means the turn is already failing.' },
  { key: 'min_vol_expansion_21d', label: 'Min volume expansion', min: 1.0, max: 3.0, step: 0.05, fmt: (v) => `${v.toFixed(2)}×`,
    desc: 'Last-5-day average volume ÷ the prior 21-day average. ≥1.2× means real participation is showing up behind the move, not a drift on light volume.' },
  { key: 'max_days_since_63d_high', label: 'Fresh quarter-high within', min: 0, max: 40, step: 1, fmt: (v) => `${Math.round(v)}d`,
    desc: 'Sessions since the last 63-day (one-quarter) closing high. Small = the name just broke out of its base to a new intermediate high.' },
  { key: 'max_days_since_sma200_reclaim', label: '200-SMA reclaim within', min: 5, max: 180, step: 5, fmt: (v) => `${Math.round(v)}d`,
    desc: 'Sessions since price first closed back above the 200-day SMA after ≥20 sessions below it — the trend-change trigger. Fail-open if there was no such dip.' },
  { key: 'max_days_since_golden_cross', label: 'Golden cross within', min: 5, max: 180, step: 5, fmt: (v) => `${Math.round(v)}d`,
    desc: 'Sessions since the 50-day SMA crossed up through the 200-day. Supporting trend-change evidence — fail-open when there is no recent cross.' },
  { key: 'min_rs_rank_delta_21', label: 'Min RS-rank gain (21d)', min: -10, max: 30, step: 1, fmt: (v) => `${v > 0 ? '+' : ''}${Math.round(v)}`,
    desc: 'Change in RS rank vs ~21 sessions ago. Positive = relative strength is accelerating, not just already high. Fail-open until the nightly scan has run for a few weeks.' },
  { key: 'min_rs_rank', label: 'Min RS rank', min: 40, max: 90, step: 1, fmt: (v) => Math.round(v),
    desc: 'A modest absolute RS floor (emerging default 60). The point of this lane is acceleration from a middling base, not a top-decile name.' },
  { key: 'max_pct_below_52w_high', label: 'Max % below 52w high', min: 0.10, max: 0.60, step: 0.05, fmt: (v) => `${(v * 100).toFixed(0)}%`,
    desc: 'A name that just turned is often 20–40% below its old 52-week high — that is expected here, so this ceiling is loose.' },
  { key: 'min_market_cap', label: 'Min market cap', min: 2e8, max: 10e9, step: 1e8, fmt: (v) => `$${(v / 1e9).toFixed(1)}B`,
    desc: 'Emerging moves show up in mid-caps first — emerging default is $300M, well below the Leaders floor.' },
  { key: 'max_ret_3m', label: 'Max 3-month return', min: 0.30, max: 2.0, step: 0.10, fmt: (v) => `${(v * 100).toFixed(0)}%`,
    desc: 'Blow-off exclude. If the name has already tripled this quarter you are late — cap the 63-day return here.' },
  { key: 'max_dist_above_50sma_atr', label: 'Max dist above 50-SMA (ATR)', min: 2.0, max: 8.0, step: 0.5, fmt: (v) => `${v.toFixed(1)}×`,
    desc: 'Price distance above the 50-day SMA measured in ATRs. Too extended and the entry risk is poor even if the trend is fresh.' },
]

const LANES = {
  leaders: {
    label: 'Leaders', icon: Trophy,
    blurb: 'Confirmed trend leaders near a pivot — full MA stack, RS rank ≥92, within a few % of the 52-week high. The promote path.',
    presets: ['strict', 'balanced', 'exploratory'], defaultPreset: 'strict', controls: CONTROLS_LEADERS,
  },
  emerging: {
    label: 'Emerging Momentum', icon: Rocket,
    blurb: 'A fresh, volume-backed turn to a new intermediate high — RS accelerating from a middling base, 200-day just reclaimed. Early discovery, not a promote path yet.',
    presets: ['emerging'], defaultPreset: 'emerging', controls: CONTROLS_EMERGING,
  },
}

const num = (v) => (v == null ? null : Number(v))
const pct = (v) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`)

function Badges({ r }) {
  const b = []
  if (r.cluster_insider_buy) b.push(['insider cluster', 'bg-emerald-900/50 text-emerald-300 border-emerald-700'])
  else if (num(r.insider_buy_sell_ratio_90d) != null && num(r.insider_buy_sell_ratio_90d) < 0.2)
    b.push(['insiders selling', 'bg-rose-900/50 text-rose-300 border-rose-700'])
  if (num(r.congress_buy_count_90d) > 0) b.push([`🏛 ${r.congress_buy_count_90d}`, 'bg-sky-900/50 text-sky-300 border-sky-700'])
  if (num(r.gov_contract_award_90d) > 0)
    b.push([`contract $${(num(r.gov_contract_award_90d) / 1e6).toFixed(0)}M${r.gov_contract_newly_awarded ? ' new' : ''}`,
      'bg-amber-900/50 text-amber-300 border-amber-700'])
  if (num(r.short_vol_ratio_5d) >= 0.55) b.push(['high short', 'bg-violet-900/50 text-violet-300 border-violet-700'])
  if (num(r.days_since_sma200_reclaim) != null && num(r.days_since_sma200_reclaim) <= 40)
    b.push([`200-SMA reclaim ${r.days_since_sma200_reclaim}d`, 'bg-indigo-900/50 text-indigo-300 border-indigo-700'])
  if (num(r.days_since_golden_cross) != null && num(r.days_since_golden_cross) <= 63)
    b.push([`golden cross ${r.days_since_golden_cross}d`, 'bg-teal-900/50 text-teal-300 border-teal-700'])
  return (
    <span className="inline-flex flex-wrap gap-1">
      {b.map(([t, cls], i) => (
        <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded border ${cls}`} style={mono}>{t}</span>
      ))}
    </span>
  )
}

export default function Discovery() {
  const [status, setStatus] = useState(null)
  const [settings, setSettings] = useState(null)
  const [descriptors, setDescriptors] = useState(null)
  const [cfg, setCfg] = useState(null)             // working (view) config
  const [lane, setLane] = useState('leaders')
  const [preset, setPreset] = useState('strict')
  const [rows, setRows] = useState([])
  const [previewCount, setPreviewCount] = useState(null)
  const [spyUp, setSpyUp] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState('')
  const [expanded, setExpanded] = useState(null)
  const debTimer = useRef(null)

  const laneDef = LANES[lane]

  const loadCandidates = useCallback(async (ln) => {
    const cand = await screener.candidates({ lane: ln, min_gate_days: 0, passed_gate: 'true' })
    setRows(cand.rows || [])
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [st, se, dc] = await Promise.all([
        screener.status(), screener.settings(), screener.config(),
      ])
      setStatus(st)
      setSettings(se.settings)
      setDescriptors(dc)
      const nightly = se.settings?.nightly_config_name || 'strict'
      const startLane = nightly === 'emerging' ? 'emerging' : 'leaders'
      setLane(startLane)
      setPreset(LANES[startLane].presets.includes(nightly) ? nightly : LANES[startLane].defaultPreset)
      setCfg(dc.presets[nightly] || dc.presets[LANES[startLane].defaultPreset] || dc.presets.strict)
      await loadCandidates(startLane)
    } finally {
      setLoading(false)
    }
  }, [loadCandidates])

  useEffect(() => { load() }, [load])

  // live preview when the working config changes. The "matches: N" counter always
  // reflects the preview; the table only switches to preview rows when the user is
  // actively tuning (preset === 'custom') — otherwise it shows what the last scan
  // actually found for this lane, which carries the lane-specific columns + badges.
  useEffect(() => {
    if (!cfg) return
    clearTimeout(debTimer.current)
    debTimer.current = setTimeout(async () => {
      try {
        const r = await screener.preview(cfg)
        setPreviewCount(r.count)
        if (r.spy_uptrend != null) setSpyUp(r.spy_uptrend)
        if (preset === 'custom') setRows(r.rows || [])
      } catch { /* keep stored rows */ }
    }, 300)
    return () => clearTimeout(debTimer.current)
  }, [cfg, preset])

  const switchLane = (ln) => {
    if (ln === lane) return
    setLane(ln)
    const p = LANES[ln].defaultPreset
    setPreset(p)
    if (descriptors?.presets[p]) setCfg({ ...descriptors.presets[p] })
    setPreviewCount(null)
    loadCandidates(ln)
  }

  const applyPreset = (name) => {
    setPreset(name)
    if (descriptors?.presets[name]) setCfg({ ...descriptors.presets[name] })
  }

  const setKey = (k, v) => {
    setPreset('custom')
    setCfg((c) => ({ ...c, [k]: v }))
  }

  const setNightly = async () => {
    if (preset === 'custom') { alert('Save the current controls as a preset first, then select it.'); return }
    if (preset !== 'strict' &&
      !window.confirm(
        `Set the nightly scan to "${preset}"?\n\n` +
        `consecutive_gate_days, the promotable bar, promote alerts and the idle ` +
        `de-listing check all evaluate against the nightly config. A looser nightly ` +
        `config surfaces more names but weakens what "confirmed leader" means.`)) return
    setBusy('nightly')
    try {
      const r = await screener.setNightlyConfig(preset, 'dashboard')
      if (r.error) { alert(r.error); return }
      if (window.confirm('Nightly config changed. Re-score the last ~20 scans under it now? (no data re-fetch)')) {
        const rs = await screener.rescore(20)
        alert(`Re-scored ${rs.rescored_dates} scans → ${rs.candidate_rows} candidate rows.`)
      }
      await load()
    } finally { setBusy('') }
  }

  const saveCustom = async () => {
    const name = window.prompt('Save the current controls as a preset named:')
    if (!name) return
    setBusy('save')
    try {
      const r = await screener.saveConfig(name, cfg)
      if (r.error) alert(r.error)
      else { alert(`Saved as "${r.saved}". Select it under Nightly scan to use it.`); load() }
    } finally { setBusy('') }
  }

  const promote = async (sym) => {
    const extra = lane === 'emerging'
      ? '\n\nNote: this is an EMERGING-momentum name — earlier and higher-variance than a Leaders candidate.'
      : ''
    if (!window.confirm(`Promote ${sym} to the watchlist? It will start collecting data and be eligible to trade.${extra}`)) return
    setBusy(sym)
    try {
      const r = await screener.promote(sym, 'dashboard')
      alert(r.error ? `Could not promote: ${r.error}${r.offenders ? ` (${r.offenders.join(', ')})` : ''}` : `Promoted ${sym}.`)
      if (!r.error) load()
    } finally { setBusy('') }
  }

  const ignore = async (sym) => {
    setBusy(sym)
    try { await screener.ignore(sym, 8); setRows((rs) => rs.filter((r) => r.symbol !== sym)) }
    finally { setBusy('') }
  }

  const lastRun = status?.runs?.[0]
  const emerging = lane === 'emerging'

  return (
    <div className="p-6 max-w-7xl mx-auto text-slate-200">
      {/* header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Radar className="w-6 h-6 text-indigo-400" />
          <div>
            <h1 className="text-xl font-semibold">Discovery</h1>
            <p className="text-sm text-slate-400" style={mono}>
              {lastRun
                ? `last scan ${String(lastRun.scan_date).slice(0, 16)} · ${lastRun.status} · ${lastRun.symbols_ok || 0} scanned`
                : 'no scan yet'} · universe {status?.universe_size ?? '—'}
              {spyUp != null && <> · SPY {spyUp ? 'uptrend' : 'DOWNTREND'}</>}
            </p>
          </div>
        </div>
        <button onClick={load} className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg border border-slate-700 hover:bg-slate-800">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <MacroOpticsPanel />

      {/* lane tabs */}
      <div className="flex gap-2 mb-2">
        {Object.entries(LANES).map(([k, def]) => {
          const Icon = def.icon
          return (
            <button key={k} onClick={() => switchLane(k)}
              className={`flex items-center gap-2 text-sm px-3.5 py-2 rounded-lg border ${lane === k
                ? 'bg-indigo-600 border-indigo-500 text-white'
                : 'border-slate-700 text-slate-300 hover:bg-slate-800'}`}>
              <Icon className="w-4 h-4" /> {def.label}
            </button>
          )
        })}
      </div>
      <p className="text-xs text-slate-500 mb-5 max-w-3xl">{laneDef.blurb}</p>

      {/* controls */}
      <div style={card} className="p-4 mb-6">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <Sliders className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-medium">Controls</span>
          <div className="ml-3 flex gap-1">
            {laneDef.presets.map((p) => (
              <button key={p} onClick={() => applyPreset(p)}
                className={`text-xs px-2.5 py-1 rounded-md border ${preset === p
                  ? 'bg-indigo-600 border-indigo-500 text-white'
                  : 'border-slate-700 text-slate-300 hover:bg-slate-800'}`}>
                {p}
              </button>
            ))}
            {preset === 'custom' && (
              <>
                <span className="text-xs px-2.5 py-1 rounded-md bg-amber-900/40 border border-amber-700 text-amber-300">custom</span>
                <button onClick={saveCustom} disabled={busy === 'save'}
                  className="text-xs px-2.5 py-1 rounded-md border border-slate-700 text-slate-300 hover:bg-slate-800">
                  Save preset…
                </button>
              </>
            )}
          </div>
          <div className="ml-auto text-sm whitespace-nowrap" style={mono}>
            matches: <span className={`font-semibold ${previewCount === 0 ? 'text-slate-500' : 'text-emerald-400'}`}>
              {previewCount ?? '…'}
            </span>
          </div>
        </div>
        <p className="text-xs text-slate-600 mb-4">
          Every slider re-evaluates the gate live over the last scan’s stored factors — no data re-fetch. Hover a label for what it does.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
          {cfg && laneDef.controls.map((c) => {
            const v = num(cfg[c.key])
            const off = v == null
            return (
              <label key={c.key} className="text-xs" title={c.desc}>
                <div className="flex justify-between mb-1">
                  <span className="text-slate-300 font-medium">{c.label}</span>
                  <span style={mono} className={off ? 'text-slate-600' : 'text-emerald-300'}>
                    {off ? 'off' : c.fmt(v)}
                  </span>
                </div>
                <input type="range" min={c.min} max={c.max} step={c.step}
                  value={off ? c.min : v}
                  onChange={(e) => setKey(c.key, Number(e.target.value))}
                  className="w-full accent-indigo-500" />
                <p className="text-[11px] leading-snug text-slate-500 mt-1">{c.desc}</p>
              </label>
            )
          })}
        </div>

        {/* nightly-config setting */}
        <div className="mt-4 pt-3 border-t border-slate-800 flex items-center gap-3 text-xs">
          <span className="text-slate-400">Nightly scan runs:</span>
          <span style={mono} className="text-slate-200">{settings?.nightly_config_name || 'strict'}</span>
          {settings?.nightly_config_updated_by && (
            <span className="text-slate-500">
              (changed {String(settings.nightly_config_updated_at).slice(0, 16)} by {settings.nightly_config_updated_by})
            </span>
          )}
          {preset !== (settings?.nightly_config_name || 'strict') && preset !== 'custom' && (
            <button onClick={setNightly} disabled={busy === 'nightly'}
              className="ml-auto px-3 py-1 rounded-md bg-amber-700 hover:bg-amber-600 text-white">
              Set nightly → {preset}
            </button>
          )}
        </div>
        <p className="text-[11px] text-slate-600 mt-2">
          The Emerging lane always runs alongside the nightly config regardless of this setting — every scan tags candidates as Leaders, Emerging, or both.
        </p>
      </div>

      {/* results */}
      <div style={card} className="overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-slate-500 border-b border-slate-800" style={mono}>
              <th className="text-left p-3">#</th>
              <th className="text-left p-3">symbol</th>
              <th className="text-right p-3">score</th>
              <th className="text-right p-3">RS</th>
              {emerging ? (
                <>
                  <th className="text-right p-3">1m ret</th>
                  <th className="text-right p-3">vol exp</th>
                  <th className="text-right p-3">63d-high</th>
                </>
              ) : (
                <>
                  <th className="text-right p-3">% &lt; 52wh</th>
                  <th className="text-right p-3">20d-high</th>
                  <th className="text-right p-3">gate days</th>
                </>
              )}
              <th className="text-left p-3">sector</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={9} className="p-8 text-center text-slate-500">
                {loading ? 'loading…'
                  : emerging ? 'no names pass the emerging gate right now — loosen a trigger or wait for the tape'
                  : 'no names pass the current config — try a looser preset'}
              </td></tr>
            )}
            {rows.map((r, i) => (
              <React.Fragment key={r.symbol}>
                <tr className="border-b border-slate-800/60 hover:bg-slate-800/30">
                  <td className="p-3 text-slate-500" style={mono}>{i + 1}</td>
                  <td className="p-3 font-semibold" style={mono}>
                    <button className="hover:text-indigo-400" onClick={() => setExpanded(expanded === r.symbol ? null : r.symbol)}>
                      {r.symbol}
                    </button>
                    {r.lane === 'both' && <span className="ml-1.5 text-[10px] text-amber-400">L+E</span>}
                  </td>
                  <td className="p-3 text-right" style={mono}>
                    {emerging ? (r.emerging_score ?? r.score ?? '—') : (r.score ?? r.composite_score ?? '—')}
                  </td>
                  <td className="p-3 text-right" style={mono}>{r.rs_rank ?? r.rs_rank_12_1 ?? '—'}</td>
                  {emerging ? (
                    <>
                      <td className="p-3 text-right" style={mono}>{pct(r.ret_21d)}</td>
                      <td className="p-3 text-right" style={mono}>{r.vol_expansion_21d != null ? `${num(r.vol_expansion_21d).toFixed(2)}×` : '—'}</td>
                      <td className="p-3 text-right" style={mono}>{r.days_since_63d_high ?? '—'}</td>
                    </>
                  ) : (
                    <>
                      <td className="p-3 text-right" style={mono}>{pct(r.pct_from_52w_high)}</td>
                      <td className="p-3 text-right" style={mono}>{r.days_since_20d_high ?? '—'}</td>
                      <td className="p-3 text-right" style={mono}>{r.consecutive_gate_days ?? '—'}</td>
                    </>
                  )}
                  <td className="p-3 text-slate-400">
                    <div>{r.sector || '—'}</div>
                    <Badges r={r} />
                  </td>
                  <td className="p-3 whitespace-nowrap text-right">
                    <button onClick={() => promote(r.symbol)} disabled={busy === r.symbol}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-emerald-700 hover:bg-emerald-600 text-white mr-1">
                      <ArrowUpRight className="w-3 h-3" /> Promote
                    </button>
                    <button onClick={() => ignore(r.symbol)} disabled={busy === r.symbol}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-slate-700 hover:bg-slate-800">
                      <EyeOff className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
                {expanded === r.symbol && <DetailRow symbol={r.symbol} emerging={emerging} />}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function DetailRow({ symbol, emerging }) {
  const [d, setD] = useState(null)
  useEffect(() => { screener.candidate(symbol).then(setD).catch(() => setD({ error: true })) }, [symbol])
  if (!d) return <tr><td colSpan={9} className="p-4 text-xs text-slate-500">loading {symbol}…</td></tr>
  if (d.error) return <tr><td colSpan={9} className="p-4 text-xs text-rose-400">no detail for {symbol}</td></tr>
  const checks = (emerging ? d.reason?.emerging?.checks : d.reason?.gate) || d.reason?.gate || []
  const comps = (emerging ? d.reason?.emerging?.components : d.reason?.score) || d.reason?.score || {}
  return (
    <tr className="bg-slate-900/60">
      <td colSpan={9} className="p-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 text-xs">
          <div>
            <div className="text-slate-400 mb-2 flex items-center gap-1">
              <Info className="w-3 h-3" /> {emerging ? 'emerging' : 'leaders'} gate lines
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1" style={mono}>
              {checks.map((c) => (
                <div key={c.key} className={c.ok ? 'text-emerald-400' : 'text-rose-400'}>
                  {c.ok ? <CheckCircle2 className="w-3 h-3 inline mr-1" /> : <XCircle className="w-3 h-3 inline mr-1" />}
                  {c.key} <span className="text-slate-500">({c.value == null ? '—' : (+c.value).toPrecision(3)} vs {String(c.threshold)})</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-slate-400 mb-2 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> score components</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1" style={mono}>
              {Object.entries(comps).map(([k, v]) => (
                <div key={k}>{k} <span className="text-slate-400">{typeof v === 'number' ? v.toFixed(1) : String(v)}</span></div>
              ))}
            </div>
          </div>
        </div>
      </td>
    </tr>
  )
}
